import type { FC, ReactNode } from 'react';

/**
 * Renderiza a resposta do assistente.
 *
 * <para>Existe porque o prompt do assistente **manda** o modelo responder com
 * tabela markdown e negrito (`PromptAssistente.cs`), e a bolha mostrava o texto
 * cru — a tabela chegava ao usuário como uma sopa de `|` e `---`. Quem pede
 * markdown tem que renderizar markdown.</para>
 *
 * <para><b>Por que não `react-markdown`:</b> o vocabulário aqui é fechado por
 * nós, não pela internet. O prompt permite negrito, itálico, código, lista e
 * tabela — e nada mais. Um parser de ~150 linhas cobre isso sem duas
 * dependências novas, e o que não for reconhecido cai como texto, que é a
 * degradação certa. Se um dia o vocabulário abrir (links, imagem, título), a
 * troca por `react-markdown` é direta.</para>
 *
 * <para><b>Segurança:</b> nada de `dangerouslySetInnerHTML`. Tudo vira elemento
 * React, então o conteúdo é escapado pelo próprio React — e boa parte desse
 * texto nasce de dado que o usuário do CRM digitou (nome de contato, anotação).</para>
 */

type Bloco =
    | { tipo: 'paragrafo'; texto: string }
    | { tipo: 'lista'; ordenada: boolean; itens: string[] }
    | { tipo: 'tabela'; cabecalho: string[]; linhas: string[][] };

const RE_ITEM_LISTA = /^\s*([-*•]|\d+[.)])\s+/;
const RE_SEPARADOR_TABELA = /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/;

/** Um número, um valor em reais ou um percentual — o que alinha à direita. */
const RE_NUMERICO = /^\s*(R\$\s*)?[+-]?[\d.,]+\s*%?\s*$/;

/**
 * Cabeçalho de número que é nome, não quantidade ("Nº", "#", "ID", "Código",
 * "Telefone", "CNPJ"): alinha à direita como número, mas nunca vira barra — a
 * barra comparava a tarefa 149 com a 28 (visto na Home em 07/10/2026) e, na
 * lista de contatos repetidos, desenhou uma barra para o telefone 554198207192
 * (22/10/2026).
 */
const RE_IDENTIFICADOR = /^\s*(n[º°o]\.?|n\.\s*[º°]|#|id|c[óo]d(igo)?\.?|n[úu]mero|telefone|celular|whatsapp|cpf|cnpj)\s*$/i;

const ehLinhaDeTabela = (linha: string) => linha.includes('|') && linha.trim().length > 1;

const celulas = (linha: string) =>
    linha
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map(c => c.trim());

/**
 * Quebra o texto em blocos. Linha vazia separa parágrafos; tabela é uma linha
 * com `|` seguida da linha de tracinhos (é a segunda linha que distingue uma
 * tabela de um texto que por acaso tem uma barra).
 */
function partirEmBlocos(texto: string): Bloco[] {
    const linhas = texto.replace(/\r\n/g, '\n').split('\n');
    const blocos: Bloco[] = [];
    let i = 0;

    while (i < linhas.length) {
        const linha = linhas[i];

        if (!linha.trim()) { i++; continue; }

        if (ehLinhaDeTabela(linha) && i + 1 < linhas.length && RE_SEPARADOR_TABELA.test(linhas[i + 1])) {
            const cabecalho = celulas(linha);
            i += 2;

            const corpo: string[][] = [];
            while (i < linhas.length && ehLinhaDeTabela(linhas[i])) {
                corpo.push(celulas(linhas[i]));
                i++;
            }

            blocos.push({ tipo: 'tabela', cabecalho, linhas: corpo });
            continue;
        }

        if (RE_ITEM_LISTA.test(linha)) {
            const ordenada = /^\s*\d+[.)]/.test(linha);
            const itens: string[] = [];

            while (i < linhas.length && RE_ITEM_LISTA.test(linhas[i])) {
                itens.push(linhas[i].replace(RE_ITEM_LISTA, ''));
                i++;
            }

            blocos.push({ tipo: 'lista', ordenada, itens });
            continue;
        }

        /* Parágrafo: junta as linhas seguintes até a linha vazia ou até começar
           outro bloco. As quebras internas são preservadas no CSS. */
        const pedacos: string[] = [];
        while (
            i < linhas.length
            && linhas[i].trim()
            && !RE_ITEM_LISTA.test(linhas[i])
            && !(ehLinhaDeTabela(linhas[i]) && i + 1 < linhas.length && RE_SEPARADOR_TABELA.test(linhas[i + 1]))
        ) {
            /* `## titulo` não faz parte do vocabulário pedido, mas se vier, vira
               texto — nunca um `#` solto na tela. */
            pedacos.push(linhas[i].replace(/^#{1,6}\s+/, ''));
            i++;
        }

        if (pedacos.length) blocos.push({ tipo: 'paragrafo', texto: pedacos.join('\n') });
    }

    return blocos;
}

/** `**negrito**`, `*itálico*` e `` `código` `` dentro de uma linha. */
function inline(texto: string, chave: string): ReactNode[] {
    const partes = texto.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g);

    return partes.filter(Boolean).map((parte, indice) => {
        const k = `${chave}-${indice}`;

        if (parte.startsWith('**') && parte.endsWith('**') && parte.length > 4) {
            return <strong key={k}>{parte.slice(2, -2)}</strong>;
        }
        if (parte.startsWith('`') && parte.endsWith('`') && parte.length > 2) {
            return <code key={k} className="as-md-cod">{parte.slice(1, -1)}</code>;
        }
        if (parte.startsWith('*') && parte.endsWith('*') && parte.length > 2) {
            return <em key={k}>{parte.slice(1, -1)}</em>;
        }
        return <span key={k}>{parte}</span>;
    });
}

/**
 * Coluna numérica alinha à direita, como em toda tabela do app — é o que deixa
 * valores comparáveis de relance. Decidido pelo CONTEÚDO e não pelo `---:` do
 * markdown, porque o modelo raramente escreve o alinhamento.
 */
const colunaEhNumerica = (linhas: string[][], indice: number) => {
    const valores = linhas.map(l => l[indice] ?? '').filter(v => v.trim());
    return valores.length > 0 && valores.every(v => RE_NUMERICO.test(v));
};

/** "R$ 1.285,00" → 1285 · "112%" → 112 · "7" → 7. Formato pt-BR. */
function numeroDe(texto: string): number | null {
    const limpo = texto.replace(/R\$|\s|%/g, '').replace(/\./g, '').replace(',', '.');
    const n = Number(limpo);
    return Number.isFinite(n) ? n : null;
}

/**
 * Qual coluna numérica vira barra.
 *
 * <para>Aplica o padrão de ranking do design system — "tabela com a barra dentro
 * da linha, com o valor absoluto ao lado" — em vez de inventar um gráfico
 * separado. <b>A barra não acrescenta informação nenhuma: é a própria célula
 * desenhada</b>, então não há como ela discordar do número nem alucinar.</para>
 *
 * <para>Só entra com 3 linhas ou mais (com duas não há o que comparar), só em
 * valores não-negativos, e prefere a coluna de percentual — que é a que responde
 * "quem está na frente".</para>
 */
function colunaDaBarra(cabecalho: string[], linhas: string[][], numericas: boolean[]): number | null {
    if (linhas.length < 3) return null;

    const candidatas = numericas
        .map((ehNum, i) => (ehNum ? i : -1))
        .filter(i => i >= 0)
        .filter(i => !RE_IDENTIFICADOR.test((cabecalho[i] ?? '').replace(/\*/g, '')))
        .filter(i => {
            const valores = linhas.map(l => numeroDe(l[i] ?? ''));
            return valores.every(v => v !== null && v >= 0) && valores.some(v => (v ?? 0) > 0);
        });

    if (candidatas.length === 0) return null;

    const percentual = candidatas.find(i =>
        /%|percent|atingi/i.test(cabecalho[i] ?? '') || linhas.some(l => (l[i] ?? '').includes('%')));

    return percentual ?? candidatas[candidatas.length - 1];
}

/** Diz se o texto tem tabela — a tela usa para dar a largura da coluna à mensagem. */
export const respostaTemTabela = (texto: string) =>
    partirEmBlocos(texto).some(b => b.tipo === 'tabela');

const AssistenteMarkdown: FC<{ texto: string }> = ({ texto }) => {
    const blocos = partirEmBlocos(texto);

    /* Sem bloco nenhum (texto só com espaços, ou algo que o parser não
       reconheceu) o conteúdo original vai para a tela como está. Melhor texto
       cru do que bolha vazia. */
    if (blocos.length === 0) return <>{texto}</>;

    return (
        <>
            {blocos.map((bloco, indice) => {
                if (bloco.tipo === 'paragrafo') {
                    return (
                        <p key={indice} className="as-md-p">
                            {inline(bloco.texto, `p${indice}`)}
                        </p>
                    );
                }

                if (bloco.tipo === 'lista') {
                    const Lista = bloco.ordenada ? 'ol' : 'ul';
                    return (
                        <Lista key={indice} className="as-md-lista">
                            {bloco.itens.map((item, j) => (
                                <li key={j}>{inline(item, `l${indice}-${j}`)}</li>
                            ))}
                        </Lista>
                    );
                }

                const numericas = bloco.cabecalho.map((_, c) => colunaEhNumerica(bloco.linhas, c));
                const cBarra = colunaDaBarra(bloco.cabecalho, bloco.linhas, numericas);

                /* Percentual usa 100 como régua (senão o primeiro colocado sempre
                   enche a barra e some a noção de "bateu ou não"); o resto usa o
                   maior valor da coluna. */
                const ehPercentual = cBarra !== null
                    && (/%/.test(bloco.cabecalho[cBarra] ?? '') || bloco.linhas.some(l => (l[cBarra] ?? '').includes('%')));
                const maximo = cBarra === null
                    ? 0
                    : Math.max(
                        ehPercentual ? 100 : 0,
                        ...bloco.linhas.map(l => numeroDe(l[cBarra] ?? '') ?? 0),
                    );

                return (
                    /* A rolagem horizontal fica no invólucro, não na tabela: é o
                       que permite a tabela ser mais larga que a bolha sem a
                       bolha crescer junto e furar a coluna da tela. */
                    <div key={indice} className="as-md-tabela-rolagem">
                        <table className="as-md-tabela">
                            <thead>
                                <tr>
                                    {bloco.cabecalho.map((celula, c) => (
                                        <th key={c} className={numericas[c] ? 'is-num' : ''}>
                                            {inline(celula, `th${indice}-${c}`)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bloco.linhas.map((linha, l) => (
                                    <tr key={l}>
                                        {bloco.cabecalho.map((_, c) => {
                                            const celula = linha[c] ?? '';

                                            if (c === cBarra && maximo > 0) {
                                                const valor = numeroDe(celula) ?? 0;
                                                /* Zero não desenha barra: um tracinho em quem não
                                                   vendeu nada sugere que vendeu pouco. Acima de
                                                   zero, 2% de piso para a barra não sumir. */
                                                const largura = valor <= 0
                                                    ? 0
                                                    : Math.max(2, Math.min(100, (valor / maximo) * 100));

                                                return (
                                                    <td key={c} className="is-num is-barra">
                                                        <span className="as-md-barra" aria-hidden="true">
                                                            <i style={{ width: `${largura}%` }} />
                                                        </span>
                                                        <span className="as-md-barra-valor">{celula}</span>
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td key={c} className={numericas[c] ? 'is-num' : ''}>
                                                    {inline(celula, `td${indice}-${l}-${c}`)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </>
    );
};

export default AssistenteMarkdown;
